<template>
  <div class="intro-y box p-5">
    <!-- Title field:start -->
    <div class="mt-6" v-if="showTitle">
      <label :for="`form-title-${kActive}`" class="form-label">
        {{ $t(`${viewRoot}.form.title.label`) }}
      </label>

      <div class="input-group">
        <input
          :id="`form-title-${kActive}`"
          type="text"
          class="form-control w-full bg-theme-32"
          :class="localData.validations.title && 'border border-theme-6'"
          :placeholder="$t(`${viewRoot}.form.title.placeholder`)"
          v-model="localData.langs[kActive].title"
          :disabled="readOnly"
        />
      </div>

      <div class="text-xs mt-1 whitespace-nowrap" :class="() => counterClass(titleLength, MAX_LENGTH_TITLE)">
        {{ titleLength }}/{{ MAX_LENGTH_TITLE }}
      </div>

      <div v-if="localData.validations.title" class="text-theme-6 mt-2">
        {{ localData.validations.title }}
      </div>
    </div>
    <!-- Title field:end -->

    <!-- Subtitle field:start -->
    <div class="mt-6" v-if="showSubtitle">
      <label :for="`form-subtitle-${kActive}`" class="form-label">
        {{ $t(`${viewRoot}.form.subtitle.label`) }}
      </label>

      <div class="input-group">
        <input
          :id="`form-subtitle-${kActive}`"
          type="text"
          class="form-control w-full bg-theme-32"
          :class="localData.validations.subtitle && 'border border-theme-6'"
          :placeholder="$t(`${viewRoot}.form.subtitle.placeholder`)"
          v-model="localData.langs[kActive].subtitle"
          :disabled="readOnly"
        />
      </div>

      <div class="text-xs mt-1 whitespace-nowrap" :class="() => counterClass(subtitleLength, MAX_LENGTH_SUBTITLE)">
        {{ subtitleLength }}/{{ MAX_LENGTH_SUBTITLE }}
      </div>

      <div v-if="localData.validations.subtitle" class="text-theme-6 mt-2">
        {{ localData.validations.subtitle }}
      </div>
    </div>
    <!-- Subtitle field:end -->

    <div class="flex flex-row w-full mt-6 gap-4" v-if="showBiologicalSex || showActionType">
      <!-- Biological Sex field:start -->
      <div class="w-1/2" v-if="showBiologicalSex">
        <label class="form-label">
          {{ $t(`${viewRoot}.form.biological_sex.label`) }}
        </label>

        <TomSelect
          v-model="localData.biological_sex"
          class="w-full cursor"
          :placeholder="$t(`${viewRoot}.form.biological_sex.placeholder`)"
          :input_hide="true"
          :disabled="readOnly"
        >
          <option v-for="(sex, sexKey) in biologicalSexKeys" :key="`biological_sex-${sexKey}`" :value="sex">
            {{ $t(`globals.biological_sex.${sex}`) }}
          </option>
        </TomSelect>
      </div>
      <!-- Biological Sex field:end -->

      <!-- Type Action field:start -->
      <div class="w-1/2" v-if="showActionType">
        <label class="form-label">
          {{ $t(`${viewRoot}.form.action_type.label`) }}
        </label>

        <TomSelect
          v-model="localData.action_type"
          class="w-full cursor"
          :placeholder="$t(`${viewRoot}.form.action_type.placeholder`)"
          :input_hide="true"
          :disabled="readOnly"
        >
          <option v-for="(action, actionKey) in actionTypeKeys" :key="`action_type-${actionKey}`" :value="action">
            {{ $t(`globals.action_type.${action}`) }}
          </option>
        </TomSelect>
      </div>
      <!-- Type Action field:end -->
    </div>

    <div class="flex flex-row w-full mt-6 gap-4" v-if="showHormonalImbalances || showDigestiveDisorders">
      <!-- Hormonal Imbalances field:start -->
      <div class="w-1/2" v-if="showHormonalImbalances">
        <label class="form-label">
          {{ $t(`${viewRoot}.form.hormonal_disbalance.label`) }}
        </label>

        <div class="input-group rounded">
          <TomSelect
            v-model="localData.hormonal_imbalances"
            class="w-full cursor"
            :placeholder="$t(`${viewRoot}.form.hormonal_disbalance.placeholder`)"
            :multiple="true"
            :disabled="readOnly"
          >
            <option
              v-for="(hormonalImbalance, hormonalImbalanceKey) in hormonalImbalancesKeys"
              :key="hormonalImbalanceKey"
              :value="hormonalImbalance"
            >
              {{ $t(`globals.hormonal_imbalances.${hormonalImbalance}`) }}
            </option>
          </TomSelect>
        </div>
      </div>
      <!-- Hormonal Imbalances field:end -->

      <!-- Digestive Disorders field:start -->
      <div class="w-1/2" v-if="showDigestiveDisorders">
        <label class="form-label">
          {{ $t(`${viewRoot}.form.digestive_disorders.label`) }}
        </label>

        <TomSelect
          v-model="localData.digestive_disorders"
          class="w-full cursor"
          :placeholder="$t(`${viewRoot}.form.digestive_disorders.placeholder`)"
          :multiple="true"
          :disabled="readOnly"
        >
          <option
            v-for="digestiveDisorder in digestiveDisorders"
            :key="`digestive_disorder-${digestiveDisorder.id}`"
            :value="digestiveDisorder.id"
          >
            {{ digestiveDisorder.name }}
          </option>
        </TomSelect>
      </div>
      <!-- Digestive Disorders field:end -->
    </div>

    <!-- Nutritional Deficiency field:start -->
    <div class="w-full mt-6" v-if="showNutritionalDeficiency">
      <label class="form-label">
        {{ $t(`${viewRoot}.form.nutritional_deficiency.label`) }}
      </label>

      <TomSelect
        v-model="localData.nutritional_deficits"
        class="w-full cursor"
        :placeholder="$t(`${viewRoot}.form.nutritional_deficiency.placeholder`)"
        :multiple="true"
        :disabled="readOnly"
      >
        <option
          v-for="nutritionalDeficit in nutritionalDeficits"
          :key="nutritionalDeficit.id"
          :value="nutritionalDeficit.id"
        >
          {{ nutritionalDeficit.name }}
        </option>
      </TomSelect>
    </div>
    <!-- Nutritional Deficiency field:end -->

    <!-- Time of Day field:start -->
    <div class="mt-6" v-if="showTimeOfDay">
      <TimeOfDayPicker
        v-model="localData"
        :time-of-day-keys="timeOfDayKeys"
        :view-root="viewRoot"
        :errors="{
          time_of_day: localData.validations.time_of_day,
          time: localData.validations.time,
          offset_minutes: localData.validations.offset_minutes,
        }"
        :read-only="readOnly"
      />
    </div>
    <!-- Time of Day field:end -->

    <!-- Age Range field:start -->
    <div class="mt-6" v-if="showAgeRange">
      <AgeRangesSelect
        v-model="localData.age_ranges"
        :label="$t(`${viewRoot}.form.age_range.label`)"
        :error="localData.validations.age_ranges"
      />
    </div>
    <!-- Age Range field:end -->

    <!-- Tags field:start -->
    <div class="mt-6" v-if="showTags">
      <SearchItemsList
        v-model="localData.tags"
        :items="tagsList"
        :label="$t(`${viewRoot}.form.tags.label`)"
        :placeholder="$t(`${viewRoot}.form.tags.placeholder`)"
        :error="localData.validations.tags"
        :read-only="readOnly"
      />
    </div>
    <!-- Tags field:end -->

    <hr class="my-6" v-if="showStatus" />

    <!-- Status field:start -->
    <label class="form-label" v-if="showStatus">
      {{ $t(`${viewRoot}.form.status.label`) }}
    </label>

    <div class="mt-2 gap-8 flex" v-if="showStatus">
      <div v-for="(status, statusKey) in statusKeys" :key="statusKey" class="form-check">
        <input
          :id="`status-${status}`"
          class="form-check-input"
          type="radio"
          name="status"
          v-model="localData.status"
          :value="status"
        />

        <label :for="`status-${status}`" class="form-check-label">
          {{ $t(`globals.status_alerts.${status}`) }}
        </label>
      </div>
    </div>
    <!-- Status field:end -->
  </div>
</template>

<script>
import TimeOfDayPicker from "@/components/time-of-day-picker/Main.vue";
import AgeRangesSelect from "@/components/age-ranges-select/Main.vue";
import SearchItemsList from "@/components/search-items-list/Main.vue";

import { ensureShape } from "@/utils/modules/alerts/index.js";

export default {
  name: "AlertsModule",
  components: { TimeOfDayPicker, AgeRangesSelect, SearchItemsList },
  props: {
    modelValue: { type: Object, required: true },
    nutritionalDeficits: { type: Array, required: false, default: () => [] },
    digestiveDisorders: { type: Array, required: false, default: () => [] },
    langs: { type: Array, required: true },
    activeLangKey: { type: String, default: "" },
    readOnly: { type: Boolean, default: false },
    tagsList: { type: Array, required: false, default: () => [] },
    // PROPS VISIBLE FIELDS
    showTitle: { type: Boolean, default: true },
    showSubtitle: { type: Boolean, default: true },
    showBiologicalSex: { type: Boolean, default: true },
    showActionType: { type: Boolean, default: true },
    showHormonalImbalances: { type: Boolean, default: true },
    showDigestiveDisorders: { type: Boolean, default: true },
    showNutritionalDeficiency: { type: Boolean, default: true },
    showTimeOfDay: { type: Boolean, default: true },
    showAgeRange: { type: Boolean, default: true },
    showTags: { type: Boolean, default: true },
    showStatus: { type: Boolean, default: true },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      viewRoot: "views.alerts",
      biologicalSexKeys: this.$global.getGenders(),
      statusKeys: this.$global.getStatusAlerts(),
      actionTypeKeys: this.$global.getActionTypesAlerts(),
      hormonalImbalancesKeys: this.$global.getHormonalImbalances(),
      timeOfDayKeys: this.$global.getTimeOfDayAlerts(),
      localData: ensureShape(this.modelValue, this.langs),
      syncingModel: false,
      MAX_LENGTH_TITLE: 80,
      MAX_LENGTH_SUBTITLE: 100,
    };
  },
  computed: {
    kActive() {
      return this.activeLangKey || (localStorage.lang ? localStorage.lang : navigator.language.split("-")[0]);
    },
    titleLength() {
      const k = this.kActive;
      return this.localData.langs[k].title?.length || 0;
    },
    subtitleLength() {
      const k = this.kActive;
      return this.localData.langs[k].subtitle?.length || 0;
    },
    counterClass(counter, max) {
      return counter <= max ? "text-green-500" : "text-red-500 font-semibold";
    },
    difficultyProxy: {
      get() {
        return String(this.localData.difficulty_level ?? 1);
      },
      set(v) {
        const n = Number(v);
        this.localData.difficulty_level = Number.isFinite(n) ? n : 1;
      },
    },
  },
  watch: {
    modelValue: {
      deep: true,
      handler(val) {
        if (this.syncingModel) return;
        const next = ensureShape(val, this.langs);
        if (JSON.stringify(next) !== JSON.stringify(this.localData)) {
          this.localData = next;
        }
      },
    },
    localData: {
      deep: true,
      handler(val) {
        this.syncingModel = true;
        this.$emit("update:modelValue", JSON.parse(JSON.stringify(val)));
        this.$nextTick(() => {
          this.syncingModel = false;
        });
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.dark .form-check-switch:checked {
  background-color: rgba(41, 49, 69, 1);
  border-color: rgba(63, 72, 101, 1);

  &::before {
    background-color: #c4e8fd;
  }
}

.disabled__row {
  opacity: 0.5 !important;
}

.text-nowrap-mode {
  white-space: nowrap;
}

.min__with {
  min-width: 33%;
}
</style>
